/**
 * feat-300 — record a clip, upload it, hand back the transcript (ADR §10.5,
 * design NOTES §3.4, §3.5, §5).
 *
 * - `getUserMedia` is called ONLY from `start()`, i.e. from a click: no
 *   permission prompt until the reviewer asks.
 * - `MediaRecorder` with the first supported of `audio/webm;codecs=opus`,
 *   `audio/mp4`, `audio/ogg;codecs=opus`.
 * - Auto-stops at {@link MAX_SECONDS}.
 * - On stop: upload, then `onTranscript(text)` — into the editable box (A2). An
 *   empty transcript is `nothing` and sends nothing.
 * - `NotAllowedError` → `denied`: the panel stays open with typing. Logged ONCE
 *   per page session at `console.info`, never louder, never in a loop (AT-17).
 * - Tracks are stopped on stop, cancel and unmount. The audio Blob lives only in
 *   this closure until the upload resolves; nothing persists it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  questionEditErrorCode,
  transcribeQuestionEditAudio,
} from '../../../../services/questionEditService';

export const MAX_SECONDS = 120;
export const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'];

export type RecorderPhase = 'idle' | 'starting' | 'recording' | 'transcribing';
export type RecorderProblem =
  | 'denied'
  | 'unavailable'
  | 'nothing'
  | 'failed'
  | 'tooLong'
  | 'unsupported';

let loggedDenied = false;
/** Test seam: the once-per-session log flag. */
export function resetRecorderLogFlag(): void {
  loggedDenied = false;
}

export function pickMimeType(): string | undefined {
  const MR = (window as unknown as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return undefined;
  return MIME_CANDIDATES.find((t) => {
    try {
      return MR.isTypeSupported(t);
    } catch {
      return false;
    }
  });
}

export interface Recorder {
  phase: RecorderPhase;
  /** Whole seconds recorded so far. */
  elapsed: number;
  problem: RecorderProblem | null;
  /** The last clip was cut at the 2-minute cap. */
  capStopped: boolean;
  start: () => Promise<void>;
  stop: () => void;
  /** Abandon a recording or an in-flight upload; nothing is sent. */
  cancel: () => void;
  clearProblem: () => void;
}

export function useRecorder(
  questionId: string,
  onTranscript: (text: string) => void,
): Recorder {
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [problem, setProblem] = useState<RecorderProblem | null>(null);
  const [capStopped, setCapStopped] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<number | null>(null);
  const capRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Security R2-H1: the recording ATTEMPT generation. `start()` captures it before
   * awaiting the permission prompt; `cancel()` and unmount (= panel close) bump it.
   * Anything that finds the generation moved — a stream granted after Cancel, or a
   * recorder stopping after Cancel — stops its tracks and never uploads. Never
   * reset: it only increases.
   */
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const clearTimers = () => {
    if (tickRef.current !== null) window.clearInterval(tickRef.current);
    if (capRef.current !== null) window.clearTimeout(capRef.current);
    tickRef.current = null;
    capRef.current = null;
  };
  const stopTracks = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
      attemptRef.current += 1;
      try {
        if (recRef.current && recRef.current.state !== 'inactive') recRef.current.stop();
      } catch {
        /* already stopped */
      }
      stopTracks();
      abortRef.current?.abort();
    };
  }, []);

  const upload = useCallback(
    async (blob: Blob) => {
      setPhase('transcribing');
      const ac = new AbortController();
      abortRef.current = ac;
      try {
        const res = await transcribeQuestionEditAudio(questionId, blob, ac.signal);
        if (!mountedRef.current || ac.signal.aborted) return;
        const text = (res.text ?? '').trim();
        if (!text) setProblem('nothing');
        else onTranscriptRef.current(text);
      } catch (e) {
        if (!mountedRef.current || ac.signal.aborted) return;
        const code = questionEditErrorCode(e);
        if (code === 'empty_audio') setProblem('nothing');
        else if (code === 'audio_too_long' || code === 'audio_too_large') setProblem('tooLong');
        else if (code === 'unsupported_audio') setProblem('unsupported');
        else setProblem('failed');
      } finally {
        if (abortRef.current === ac) abortRef.current = null;
        // QA R3-L2: an ABORTED upload belongs to an attempt that was cancelled (or
        // unmounted). Whoever aborted it already owns the phase, possibly a NEW
        // recording by now. Resetting to idle here would hide that recording's bar
        // while its mic stays live, and let a third start orphan it.
        if (mountedRef.current && !ac.signal.aborted) setPhase('idle');
      }
    },
    [questionId],
  );

  const stop = useCallback(() => {
    clearTimers();
    const rec = recRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    else stopTracks();
  }, []);

  const start = useCallback(async () => {
    if (phase !== 'idle') return;
    setProblem(null);
    setCapStopped(false);
    const md = navigator.mediaDevices;
    const MR = (window as unknown as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
    if (!md || typeof md.getUserMedia !== 'function' || !MR) {
      setProblem('unavailable');
      return;
    }
    attemptRef.current += 1;
    const gen = attemptRef.current;
    const live = () => mountedRef.current && attemptRef.current === gen;
    setPhase('starting');
    let stream: MediaStream;
    try {
      stream = await md.getUserMedia({ audio: true });
    } catch (e) {
      // Cancelled (or unmounted) while the prompt was up: no problem to show.
      if (!live()) return;
      const name = (e as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
        setProblem('denied');
        if (!loggedDenied) {
          loggedDenied = true;
          // eslint-disable-next-line no-console
          console.info('[QuestionEdit] microphone permission denied; typing still works');
        }
      } else {
        setProblem('unavailable');
      }
      setPhase('idle');
      return;
    }
    if (!live()) {
      // R2-H1: permission arrived AFTER Cancel / Esc / close. Release the mic at
      // once; record nothing, upload nothing. (`cancel()` already set idle.)
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    streamRef.current = stream;
    const mimeType = pickMimeType();
    let rec: MediaRecorder;
    try {
      rec = mimeType ? new MR(stream, { mimeType }) : new MR(stream);
    } catch {
      stopTracks();
      setProblem('unsupported');
      setPhase('idle');
      return;
    }
    recRef.current = rec;
    // This attempt's OWN buffer. A cancelled recorder fires its final
    // `dataavailable` and `stop` asynchronously, possibly after a NEW attempt has
    // started, so neither handler may touch shared state once its generation is
    // stale (QA R3-L1). Otherwise the cancelled audio would land in the new
    // attempt's buffer and upload with it, or the late stop would kill the new
    // attempt's mic, timers and recorder.
    const chunks: Blob[] = [];
    chunksRef.current = chunks;
    rec.ondataavailable = (ev: BlobEvent) => {
      if (!live()) return;
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };
    rec.onstop = () => {
      if (!live()) {
        // Stale (cancelled / unmounted / superseded): release only THIS attempt's
        // mic and drop its audio. `cancel()` already set the phase.
        stream.getTracks().forEach((t) => t.stop());
        if (recRef.current === rec) recRef.current = null;
        if (streamRef.current === stream) streamRef.current = null;
        chunks.length = 0;
        return;
      }
      clearTimers();
      stopTracks();
      recRef.current = null;
      chunksRef.current = [];
      const type = rec.mimeType || mimeType || 'audio/webm';
      upload(new Blob(chunks, { type }));
    };
    startedAtRef.current = Date.now();
    setElapsed(0);
    try {
      rec.start();
    } catch {
      // R2-L1: the recorder would not start. Detach it so nothing can upload,
      // release the mic, and fall back to typing.
      rec.ondataavailable = null;
      rec.onstop = null;
      recRef.current = null;
      chunksRef.current = [];
      stopTracks();
      setProblem('unavailable');
      setPhase('idle');
      return;
    }
    setPhase('recording');
    tickRef.current = window.setInterval(() => {
      setElapsed(Math.min(MAX_SECONDS, Math.floor((Date.now() - startedAtRef.current) / 1000)));
    }, 250);
    capRef.current = window.setTimeout(() => {
      setCapStopped(true);
      setElapsed(MAX_SECONDS);
      stop();
    }, MAX_SECONDS * 1000);
  }, [phase, upload, stop]);

  const cancel = useCallback(() => {
    clearTimers();
    attemptRef.current += 1;
    const rec = recRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
    // Security R3-N1: release the mic NOW rather than on the recorder's `stop`
    // event, so a browser that never fires it can't leave the mic live. The
    // stale `onstop` only stops its own (already-stopped) tracks: idempotent.
    stopTracks();
    abortRef.current?.abort();
    setPhase('idle');
  }, []);

  const clearProblem = useCallback(() => setProblem(null), []);

  return { phase, elapsed, problem, capStopped, start, stop, cancel, clearProblem };
}
